<div class="pp-page">

  <app-prevention-group-header activeTab="overview">
    <div class="period-selector">
      @for (d of [7, 30, 90]; track d) {
        <button class="period-btn" [class.active]="periodDays() === d" (click)="setPeriodDays(d)">
          {{ 'pilotagePrevention.period.' + d | translate }}
        </button>
      }
    </div>
  </app-prevention-group-header>

  <p class="page-subtitle">{{ 'pilotagePrevention.overview.subtitle' | translate }}</p>

  @if (loadError()) {
    <div class="cs" style="text-align:center;padding:40px 0">{{ 'pilotagePrevention.states.error' | translate }}</div>
  } @else if (!dashboard()) {
    <div class="cs" style="text-align:center;padding:40px 0">{{ 'pilotagePrevention.states.loading' | translate }}</div>
  } @else {

    <!-- 1. État du programme : 4 KPI maximum -->
    <div class="card" style="margin-bottom:20px">
      <div class="overview-grid">
        <div class="kpi-card kpi-card--clickable" role="link" tabindex="0" (click)="goToOffers()" (keydown.enter)="goToOffers()">
          <div class="kpi-head">
            <span class="kpi-icon-badge"><span class="kpi-icon">📍</span></span>
            <span class="kpi-label">
              {{ 'pilotagePrevention.overview.kpi.deployed' | translate }}
              <span class="kpi-label-info" [attr.title]="'pilotagePrevention.overview.kpi.deployedTooltip' | translate">ⓘ</span>
            </span>
          </div>
          <div class="kpi-value">{{ dashboard()!.overview.sitesWithPrevention }} / {{ dashboard()!.overview.totalSites }}</div>
          <div class="kpi-action">{{ 'pilotagePrevention.overview.kpi.seeDeployment' | translate }} →</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-head">
            <span class="kpi-icon-badge"><span class="kpi-icon">🧭</span></span>
            <span class="kpi-label">{{ 'pilotagePrevention.overview.kpi.journeys' | translate }}</span>
          </div>
          <div class="kpi-value">@if (dashboard()!.funnel.insufficientData) {
<span class="cs" data-testid="group-funnel-insufficient">{{ 'besoinsCouverture.states.insufficientData' | translate }}</span>
} @else {
{{ dashboard()!.funnel.completed }}
}</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-head">
            <span class="kpi-icon-badge"><span class="kpi-icon">⭐</span></span>
            <span class="kpi-label">{{ 'pilotagePrevention.overview.kpi.interested' | translate }}</span>
          </div>
          <div class="kpi-value">{{ dashboard()!.funnel.interested }}</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-head">
            <span class="kpi-icon-badge"><span class="kpi-icon">💰</span></span>
            <span class="kpi-label">
              {{ 'pilotagePrevention.overview.kpi.potential' | translate }}
              <span class="kpi-label-info" [attr.title]="'pilotagePrevention.overview.kpi.potentialTooltip' | translate">ⓘ</span>
            </span>
          </div>
          <div class="kpi-value">{{ formatPrice(dashboard()!.overview.opportunitiesValue, dashboard()!.overview.currency) }}</div>
        </div>
      </div>
    </div>

    <!-- 2. À retenir (maximum 3 insights) -->
    @if (insights().length > 0) {
      <div class="card insight-card" style="margin-bottom:20px">
        <div class="ct">{{ 'pilotagePrevention.overview.insight.title' | translate }}</div>
        <div class="insight-list">
          @for (insight of insights(); track $index) {
            <div class="insight-item">
              @switch (insight.kind) {
                @case ('topUncovered') {
                  <div class="insight-text">{{ 'pilotagePrevention.overview.insight.topUncovered' | translate: { need: insight.needLabel } }}</div>
                  <div class="cs">{{ (insight.uncoveredSiteCount > 1 ? 'pilotagePrevention.overview.insight.topUncoveredDetailPlural' : 'pilotagePrevention.overview.insight.topUncoveredDetailSingular') | translate: { count: insight.uncoveredCount, sites: insight.uncoveredSiteCount } }}</div>
                  <button class="insight-cta" (click)="goToNeeds()">{{ 'pilotagePrevention.overview.insight.viewNeeds' | translate }} →</button>
                }
                @case ('topicToExplore') {
                  <div class="insight-text">{{ 'pilotagePrevention.overview.insight.topicToExplore' | translate: { topic: insight.topicLabel } }}</div>
                  <button class="insight-cta" (click)="goToCampaign()">{{ 'pilotagePrevention.overview.insight.viewCampaign' | translate }} →</button>
                }
                @case ('siteToWatch') {
                  <div class="insight-text">{{ 'pilotagePrevention.overview.insight.siteToWatch' | translate: { site: insight.siteName, rate: formatRate(insight.completionRate) } }}</div>
                }
                @case ('goodCoverage') {
                  <div class="insight-text">{{ 'pilotagePrevention.overview.insight.goodCoverage' | translate: { rate: formatRate(insight.rate) } }}</div>
                }
              }
            </div>
          }
        </div>
      </div>
    }

    <!-- 3. Parcours prévention : funnel compact, 1-2 ratios seulement.
         "Expositions" (Lot Prevention First UX Phase 1, correctif 2026-09-07) est délibérément EXCLU
         de la chaîne à flèches : c'est un événement client (PREVENTION_EXPOSED) qui précède
         l'existence même de la session (voir PreventionSessionService#trackFrontendEvent) et n'est
         donc jamais une garantie de sur-ensemble de "Démarrages" -- un funnel amont→aval avec flèches
         suppose l'inclusion stricte, ce qui n'est vrai qu'à partir de Démarrages (même cohorte SQL,
         voir PreventionGroupAnalyticsRepository#funnelCohortCounts). Restituer "Expositions" comme
         une mesure indépendante, jamais reliée par une flèche, évite de présenter un faux funnel de
         conversion quand l'exposition mesurée est inférieure aux démarrages (mesure best-effort,
         perdue avant la création de toute session). -->
    <div class="card card--secondary" style="margin-bottom:20px">
      <div class="ct ct--secondary">{{ 'pilotagePrevention.overview.journey.title' | translate }}</div>
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:14px">
        <span class="cs">
          {{ 'pilotagePrevention.overview.journey.exposed' | translate }}
          <span class="kpi-label-info" [attr.title]="'pilotagePrevention.overview.journey.exposedTooltip' | translate">ⓘ</span>
        </span>
        <span class="exposed-value">{{ dashboard()!.funnel.exposed }}</span>
      </div>
      <div class="compact-funnel">
        <div class="compact-funnel-step">
          <span class="compact-funnel-value">@if (dashboard()!.funnel.insufficientData) {
<span class="cs" data-testid="group-funnel-insufficient">{{ 'besoinsCouverture.states.insufficientData' | translate }}</span>
} @else {
{{ dashboard()!.funnel.started }}
}</span>
          <span class="compact-funnel-label">{{ 'pilotagePrevention.overview.journey.started' | translate }}</span>
        </div>
        <span class="compact-funnel-arrow">→</span>
        <div class="compact-funnel-step">
          <span class="compact-funnel-value">@if (dashboard()!.funnel.insufficientData) {
<span class="cs" data-testid="group-funnel-insufficient">{{ 'besoinsCouverture.states.insufficientData' | translate }}</span>
} @else {
{{ dashboard()!.funnel.completed }}
}</span>
          <span class="compact-funnel-label">{{ 'pilotagePrevention.overview.journey.completed' | translate }}</span>
        </div>
        <span class="compact-funnel-arrow">→</span>
        <div class="compact-funnel-step">
          <span class="compact-funnel-value">{{ dashboard()!.funnel.recommendationReached }}</span>
          <span class="compact-funnel-label">{{ 'pilotagePrevention.overview.journey.recommendationReached' | translate }}</span>
        </div>
        <span class="compact-funnel-arrow">→</span>
        <div class="compact-funnel-step compact-funnel-step--final">
          <span class="compact-funnel-value">{{ dashboard()!.funnel.interested }}</span>
          <span class="compact-funnel-label">{{ 'pilotagePrevention.overview.journey.interested' | translate }}</span>
        </div>
      </div>
      <div class="compact-funnel-ratios">
        <span class="cs">{{ 'pilotagePrevention.overview.journey.completionRate' | translate }} @if (dashboard()!.funnel.insufficientData) {
<span class="cs" data-testid="group-funnel-insufficient">{{ 'besoinsCouverture.states.insufficientData' | translate }}</span>
} @else {
<strong>{{ formatRate(dashboard()!.funnel.completionRate) }}</strong>
}</span>
        <span class="cs">{{ 'pilotagePrevention.overview.journey.interestRate' | translate }} <strong>{{ formatRate(dashboard()!.funnel.interestRate) }}</strong></span>
      </div>
      @if ((dashboard()!.funnel.excludedSiteCount ?? 0) > 0 && !dashboard()!.funnel.insufficientData) {
        <div class="cs" style="margin-top:8px" data-testid="group-funnel-excluded">{{ 'pilotagePrevention.overview.journey.excludedSites' | translate }}</div>
      }
    </div>

    <!-- 4. Où agir ? -->
    <div class="card" style="margin-bottom:20px">
      <div class="ct">{{ 'pilotagePrevention.overview.actions.title' | translate }}</div>
      <div class="action-grid">
        <div class="action-card" role="link" tabindex="0" (click)="goToNeeds()" (keydown.enter)="goToNeeds()">
          <div class="action-card-title">{{ 'pilotagePrevention.overview.actions.needs.title' | translate }}</div>
          <div class="cs">
            @if (uncoveredNeedsCount() == null) {
              {{ 'besoinsCouverture.states.insufficientData' | translate }}
            } @else if (uncoveredNeedsCount()! > 0) {
              {{ (uncoveredNeedsCount()! > 1 ? 'pilotagePrevention.overview.actions.needsSummaryPlural' : 'pilotagePrevention.overview.actions.needsSummarySingular') | translate: { count: uncoveredNeedsCount() } }}
            } @else {
              {{ 'pilotagePrevention.overview.actions.needsSummaryEmpty' | translate }}
            }
          </div>
          <span class="action-card-cta">{{ 'pilotagePrevention.overview.actions.needs.cta' | translate }} →</span>
        </div>
        <div class="action-card" role="link" tabindex="0" (click)="goToCampaign()" (keydown.enter)="goToCampaign()">
          <div class="action-card-title">{{ 'pilotagePrevention.overview.actions.campaign.title' | translate }}</div>
          <div class="cs">
            @if (topicsToExploreCount() == null) {
              {{ 'interetPatients.states.insufficientData' | translate }}
            } @else if (topicsToExploreCount()! > 0) {
              {{ (topicsToExploreCount()! > 1 ? 'pilotagePrevention.overview.actions.campaignSummaryPlural' : 'pilotagePrevention.overview.actions.campaignSummarySingular') | translate: { count: topicsToExploreCount() } }}
            } @else {
              {{ 'pilotagePrevention.overview.actions.campaignSummaryEmpty' | translate }}
            }
          </div>
          <span class="action-card-cta">{{ 'pilotagePrevention.overview.actions.campaign.cta' | translate }} →</span>
        </div>
        <div class="action-card" role="link" tabindex="0" (click)="goToOffers()" (keydown.enter)="goToOffers()">
          <div class="action-card-title">{{ 'pilotagePrevention.overview.actions.offers.title' | translate }}</div>
          <div class="cs">
            {{ 'pilotagePrevention.overview.actions.offersSummary' | translate: { count: trackedOffersCount() } }}
            <span class="kpi-label-info" [attr.title]="'pilotagePrevention.overview.actions.offersSummaryTooltip' | translate">ⓘ</span>
          </div>
          <span class="action-card-cta">{{ 'pilotagePrevention.overview.actions.offers.cta' | translate }} →</span>
        </div>
      </div>
    </div>

    <!-- 5. Performance réseau : résumé uniquement, jamais un tableau complet -->
    <div class="card card--secondary">
      <div class="ct ct--secondary">{{ 'pilotagePrevention.overview.performance.title' | translate }}</div>
      @if (networkPerformance().bestSite || networkPerformance().bestOffer) {
        <div class="perf-grid">
          @if (networkPerformance().bestSite; as s) {
            <div class="perf-item perf-item--clickable" role="link" tabindex="0" (click)="openSite(s.establishmentId)" (keydown.enter)="openSite(s.establishmentId)">
              <span class="perf-badge perf-badge--good">{{ 'pilotagePrevention.overview.performance.bestSite' | translate }}</span>
              <div class="perf-item-name">{{ s.establishmentName }}</div>
              <div class="perf-item-value">{{ formatRate(s.completionRate) }}</div>
              <div class="perf-item-metric">{{ 'pilotagePrevention.overview.performance.metricCompletionRate' | translate }}</div>
            </div>
          }
          @if (networkPerformance().siteToWatch; as s) {
            <div class="perf-item perf-item--clickable" role="link" tabindex="0" (click)="openSite(s.establishmentId)" (keydown.enter)="openSite(s.establishmentId)">
              <span class="perf-badge perf-badge--watch">{{ 'pilotagePrevention.overview.performance.siteToWatch' | translate }}</span>
              <div class="perf-item-name">{{ s.establishmentName }}</div>
              <div class="perf-item-value">{{ formatRate(s.completionRate) }}</div>
              <div class="perf-item-metric">{{ 'pilotagePrevention.overview.performance.metricCompletionRate' | translate }}</div>
            </div>
          }
          @if (networkPerformance().bestOffer; as o) {
            <div class="perf-item">
              <span class="perf-badge perf-badge--good">{{ 'pilotagePrevention.overview.performance.bestOffer' | translate }}</span>
              <div class="perf-item-name">{{ o.offerName }}</div>
              <div class="perf-item-value">{{ formatPrice(o.opportunityValue, dashboard()!.overview.currency) }}</div>
              <div class="perf-item-metric">{{ 'pilotagePrevention.overview.performance.metricPotential' | translate }}</div>
            </div>
          }
          @if (networkPerformance().offerToOptimize; as o) {
            <div class="perf-item">
              <span class="perf-badge perf-badge--watch">{{ 'pilotagePrevention.overview.performance.offerToOptimize' | translate }}</span>
              <div class="perf-item-name">{{ o.offerName }}</div>
              <div class="perf-item-value">{{ formatRate(o.interestRate) }}</div>
              <div class="perf-item-metric">{{ 'pilotagePrevention.overview.performance.metricInterestRate' | translate }}</div>
            </div>
          }
        </div>
      } @else {
        <div class="cs" style="text-align:center;padding:20px 0">{{ 'pilotagePrevention.overview.performance.empty' | translate }}</div>
      }
    </div>
  }
</div>
